import React, { useEffect, useMemo, useRef } from "react";
import { Plus, Trash2 } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { AppButton, FormField, IconButton, Input, NativeSelect, cn } from "../../ui";
import { DIFFICULTY_LABELS, blankRule } from "./builderRules";

const keyOf = (r) => `${r.category}|${r.subcategory}|${r.difficulty}`;

/**
 * A section's random draw: each rule takes `count` questions at random from the
 * bank questions it matches, and every student gets a fresh draw.
 *
 * Each rule says how many questions it matches today, read from the bank —
 * asking for 30 from a pool of 12 gives students 12, and the author should know
 * that before publishing, not after.
 */
export default function RandomRulesEditor({ rules, onChange, categories, disabled, idPrefix }) {
  const topCategories = useMemo(() => categories.filter((c) => !c.parentCategory), [categories]);
  // The counts arrive after a network round-trip; they are applied to the rules
  // as they are THEN, so a count typed meanwhile is not written back over.
  const latest = useRef({ rules, onChange });
  latest.current = { rules, onChange };
  const subsOf = (cid) => categories.filter((c) => String(c.parentCategory?._id || c.parentCategory || "") === String(cid));

  const update = (i, patch) => {
    const next = rules.map((r, j) => (j === i ? { ...r, ...patch } : r));
    if (patch.category !== undefined) next[i].subcategory = "";
    onChange(next);
  };

  // How many bank questions each rule matches — one light count per distinct
  // rule, refreshed when its filters change.
  const signature = rules.map(keyOf).join(";");
  useEffect(() => {
    let alive = true;
    const timer = setTimeout(async () => {
      const distinct = [...new Set(latest.current.rules.map(keyOf))];
      const counts = new Map();
      await Promise.all(distinct.map(async (k) => {
        const [category, subcategory, difficulty] = k.split("|");
        const params = { page: 1, limit: 1 };
        if (category) params.category = category;
        if (subcategory) params.subcategory = subcategory;
        if (difficulty) params.difficulty = difficulty;
        try {
          const { data } = await apiClient.get("/assessments/questions-for-selection", { params });
          counts.set(k, data.total ?? 0);
        } catch {
          counts.set(k, null);
        }
      }));
      if (!alive) return;
      const now = latest.current.rules;
      const next = now.map((r) => (counts.has(keyOf(r)) ? { ...r, available: counts.get(keyOf(r)) } : r));
      if (next.some((r, i) => r.available !== now[i].available)) latest.current.onChange(next);
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const drawn = rules.reduce((sum, r) => sum + (Number(r.count) || 0), 0);

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {rules.length === 0 && (
        <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
          Add a rule — for example "10 easy questions from Quantitative aptitude".
        </p>
      )}
      {rules.map((r, i) => {
        const subs = r.category ? subsOf(r.category) : [];
        const short = r.available != null && Number(r.count) > r.available;
        return (
          <div key={i} className="tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface tw-p-3">
            <div className="tw-grid tw-items-end tw-gap-2 min-[768px]:tw-grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,0.9fr)_6rem_auto]">
              <FormField label="Category">
                <NativeSelect value={r.category} onChange={(e) => update(i, { category: e.target.value })} disabled={disabled} id={`${idPrefix}-cat-${i}`}>
                  <option value="">Any category</option>
                  {topCategories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </NativeSelect>
              </FormField>
              <FormField label="Subtopic">
                <NativeSelect
                  value={r.subcategory}
                  onChange={(e) => update(i, { subcategory: e.target.value })}
                  disabled={disabled || !r.category || subs.length === 0}
                  id={`${idPrefix}-sub-${i}`}
                >
                  <option value="">{r.category && subs.length === 0 ? "No subtopics" : "Any subtopic"}</option>
                  {subs.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </NativeSelect>
              </FormField>
              <FormField label="Difficulty">
                <NativeSelect value={r.difficulty} onChange={(e) => update(i, { difficulty: e.target.value })} disabled={disabled} id={`${idPrefix}-dif-${i}`}>
                  <option value="">Any</option>
                  {Object.entries(DIFFICULTY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </NativeSelect>
              </FormField>
              <FormField label="How many">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={r.count}
                  onChange={(e) => update(i, { count: e.target.value === "" ? "" : Math.max(0, Number.parseInt(e.target.value, 10) || 0) })}
                  disabled={disabled}
                  id={`${idPrefix}-count-${i}`}
                />
              </FormField>
              <IconButton
                icon={Trash2}
                label={`Remove rule ${i + 1}`}
                variant="ghost-destructive"
                onClick={() => onChange(rules.filter((_, j) => j !== i))}
                disabled={disabled}
              />
            </div>
            <p className={cn("tw-m-0 tw-mt-2 tw-text-xs", short ? "tw-text-warning-fg" : "tw-text-content-subtle")}>
              {r.available == null
                ? "Counting matching questions…"
                : short
                  ? `Only ${r.available} question${r.available === 1 ? "" : "s"} match — each student gets ${r.available}.`
                  : `${r.available.toLocaleString("en-IN")} question${r.available === 1 ? "" : "s"} match this rule.`}
            </p>
          </div>
        );
      })}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <AppButton type="button" size="sm" variant="outline" onClick={() => onChange([...rules, blankRule()])} disabled={disabled}>
          <Plus size={16} aria-hidden="true" /> Add a rule
        </AppButton>
        {drawn > 0 && (
          <span className="tw-text-[0.8125rem] tw-text-content-muted">
            Each student gets <strong className="tw-text-content">{drawn}</strong> random question{drawn === 1 ? "" : "s"}.
          </span>
        )}
      </div>
    </div>
  );
}
